import { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  calculateGlobalScore,
  calculateENPS,
  calculateDimensionScores,
  calculateHeatmap,
  getOpenComments,
  getClimateInterpretation,
  getEnpsInterpretation,
  LIKERT_DIMENSIONS,
  type ClimateResponse,
} from '@/lib/climateScoring';
import { cn } from '@/lib/utils';
import { generateClimatePdf } from '@/lib/climatePdfReport';
import { FileDown, Loader2 } from 'lucide-react';
import type { ClimateAssessment, AssessmentCampaign } from '@/types';
import type { ClimateDimension } from '@/config/climateQuestions';

const DIMENSION_LABELS: Record<ClimateDimension, string> = {
  leadership: 'Liderazgo',
  communication: 'Comunicación',
  growth: 'Crecimiento',
  wellbeing: 'Bienestar',
  belonging: 'Pertenencia',
  loyalty: 'Lealtad',
  general: 'General',
};

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function getCellBgClass(score: number): string {
  if (score >= 80) return 'bg-green-100 dark:bg-green-900/30';
  if (score >= 60) return 'bg-amber-50 dark:bg-amber-900/20';
  return 'bg-red-50 dark:bg-red-900/20';
}

export function ClimateResultsPage() {
  const { appUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdFromUrl = searchParams.get('campaignId');
  const companyId = appUser?.companyId ?? null;
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [assessments, setAssessments] = useState<ClimateAssessment[]>([]);
  const [campaigns, setCampaigns] = useState<AssessmentCampaign[]>([]);
  const [employeeDepartments, setEmployeeDepartments] = useState<Record<string, string>>({});

  const selectedCampaignId = campaignIdFromUrl ?? '';

  const filteredAssessments = useMemo(() => {
    if (!selectedCampaignId) return assessments;
    return assessments.filter((a) => a.campaignId === selectedCampaignId);
  }, [assessments, selectedCampaignId]);

  const responses = useMemo((): ClimateResponse[] => {
    return filteredAssessments
      .filter((a) => a.status === 'completed' && a.answers && Object.keys(a.answers).length > 0)
      .map((a) => ({
        assessmentId: a.id,
        department: a.employeeId ? employeeDepartments[a.employeeId] : undefined,
        answers: a.answers ?? {},
      }));
  }, [filteredAssessments, employeeDepartments]);

  const globalScore = useMemo(() => calculateGlobalScore(responses), [responses]);
  const enpsResult = useMemo(() => calculateENPS(responses), [responses]);
  const dimensionScores = useMemo(() => calculateDimensionScores(responses), [responses]);
  const heatmapRows = useMemo(() => calculateHeatmap(responses), [responses]);
  const openComments = useMemo(() => getOpenComments(responses), [responses]);

  const totalInvited = filteredAssessments.length;
  const totalCompleted = responses.length;
  const participationPercent = totalInvited > 0 ? Math.round((totalCompleted / totalInvited) * 100) : 0;

  const radarData = useMemo(
    () =>
      dimensionScores.map((d) => ({
        dimension: DIMENSION_LABELS[d.dimension],
        score: d.score,
        fullMark: 100,
      })),
    [dimensionScores]
  );

  const barData = useMemo(
    () =>
      heatmapRows.map((r) => ({
        name: r.department,
        score: r.average,
      })),
    [heatmapRows]
  );

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [climateSnap, employeesSnap, campaignsSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, 'climate_assessments'),
              where('companyId', '==', companyId)
            )
          ),
          getDocs(
            query(
              collection(db, 'employees'),
              where('companyId', '==', companyId),
              where('isActive', '==', true)
            )
          ),
          getDocs(
            query(
              collection(db, 'assessment_campaigns'),
              where('companyId', '==', companyId),
              where('type', '==', 'CLIMATE'),
              orderBy('createdAt', 'desc')
            )
          ),
        ]);

        const deptMap: Record<string, string> = {};
        employeesSnap.docs.forEach((d) => {
          const data = d.data();
          const dep = data.department as string | undefined;
          if (d.id) deptMap[d.id] = dep?.trim() || 'Sin especificar';
        });

        const list = climateSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            companyId: data.companyId as string,
            campaignId: (data.campaignId as string) ?? 'temp-campaign-id',
            employeeId: data.employeeId as string | undefined,
            employeeName: data.employeeName as string | undefined,
            status: data.status as 'pending' | 'completed',
            createdAt: data.createdAt as string,
            completedAt: data.completedAt as string | undefined,
            answers: data.answers as Record<string, number | string> | undefined,
          } as ClimateAssessment;
        });
        list.sort((a, b) => {
          const dateA = a.completedAt || a.createdAt || '';
          const dateB = b.completedAt || b.createdAt || '';
          return dateB.localeCompare(dateA);
        });

        const campaignList = campaignsSnap.docs.map((d) => {
          const data = d.data();
          const createdAt = data.createdAt;
          let createdAtStr = new Date().toISOString();
          if (createdAt) {
            const ts = createdAt as { toDate?: () => Date };
            if (typeof ts.toDate === 'function') createdAtStr = ts.toDate().toISOString();
            else if (typeof createdAt === 'string') createdAtStr = createdAt;
          }
          return {
            id: d.id,
            companyId: data.companyId as string,
            title: data.title as string,
            type: 'CLIMATE' as const,
            status: data.status as 'draft' | 'active' | 'closed',
            createdAt: createdAtStr,
          } as AssessmentCampaign;
        });

        setAssessments(list);
        setCampaigns(campaignList);
        setEmployeeDepartments(deptMap);
      } catch (err) {
        console.error('Error cargando resultados de clima:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId]);

  if (!companyId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-muted-foreground">Acceso restringido: No tienes empresa asignada</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-muted-foreground">Cargando resultados...</p>
      </div>
    );
  }

  const handleExportPdf = async () => {
    if (!companyId) return;
    setIsExporting(true);
    try {
      const companySnap = await getDoc(doc(db, 'companies', companyId));
      const logoUrl = companySnap.exists() ? (companySnap.data()?.branding as { logoUrl?: string } | undefined)?.logoUrl : undefined;
      await generateClimatePdf({
        survey: {
          title: 'Resultados de Clima Organizacional',
          date: latestDate
            ? new Date(latestDate).toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }),
          status: surveyClosed ? 'Cerrada' : 'Activa',
        },
        stats: {
          globalScore,
          enps: { score: enpsResult.score, label: enpsResult.label },
          participation: {
            percent: participationPercent,
            completed: totalCompleted,
            total: totalInvited || 1,
          },
        },
        dimensionScores,
        heatmapData: heatmapRows,
        comments: openComments,
        logoUrl,
      });
    } catch (err) {
      console.error('Error al exportar PDF:', err);
      alert('No se pudo generar el PDF. Prueba de nuevo.');
    } finally {
      setIsExporting(false);
    }
  };

  const surveyClosed = totalCompleted > 0 && totalCompleted >= totalInvited;
  const latestDate =
    filteredAssessments.length > 0
      ? filteredAssessments
          .map((a) => a.completedAt || a.createdAt)
          .filter(Boolean)
          .sort()
          .reverse()[0]
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/nom035">← Desarrollo Organizacional</Link>
        </Button>
        {campaigns.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="campaign-select" className="text-sm font-medium text-muted-foreground">
              Campaña:
            </label>
            <select
              id="campaign-select"
              value={selectedCampaignId}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setSearchParams({ campaignId: v });
                else setSearchParams({});
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="">Todas</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title} {c.status === 'active' ? '(activa)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Resultados de Clima Organizacional</h1>
            <p className="text-sm text-muted-foreground">
              {latestDate
                ? `Última actualización: ${new Date(latestDate).toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}`
                : 'Sin datos de encuestas'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={surveyClosed ? 'secondary' : 'default'}>
              {surveyClosed ? 'Cerrada' : 'Activa'}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-2 h-4 w-4" />
              )}
              {isExporting ? 'Generando…' : 'Exportar PDF'}
            </Button>
          </div>
        </div>

        {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Índice de Clima</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className={cn('text-4xl font-bold tabular-nums', getClimateInterpretation(globalScore).color)}>
              {globalScore}
            </p>
            <p className="text-sm text-muted-foreground">/ 100</p>
            <p className={cn('text-sm font-medium', getClimateInterpretation(globalScore).color)}>
              {getClimateInterpretation(globalScore).label}
            </p>
            <p className="text-xs text-muted-foreground italic">
              {getClimateInterpretation(globalScore).description}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>eNPS</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <p
              className={cn(
                'text-4xl font-bold tabular-nums',
                getEnpsInterpretation(enpsResult.score).color
              )}
            >
              {enpsResult.score >= 0 ? '+' : ''}
              {enpsResult.score}
            </p>
            <p className={cn('text-sm font-medium', getEnpsInterpretation(enpsResult.score).color)}>
              {getEnpsInterpretation(enpsResult.score).label}
            </p>
            <p className="text-xs text-muted-foreground italic">
              {getEnpsInterpretation(enpsResult.score).description}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Participación</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{participationPercent}%</span>
                <span className="text-muted-foreground">
                  {totalCompleted} / {totalInvited || 1} empleados
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${participationPercent}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {responses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <p className="text-muted-foreground">No hay respuestas completadas aún.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Genera enlaces desde la pestaña Clima Organizacional y espera a que los empleados respondan.
            </p>
            <Button className="mt-4" asChild>
              <Link to="/app/nom035">Ir a Desarrollo Organizacional</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Gráficos */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Balance por Dimensiones</CardTitle>
                <CardDescription>Radar de las 5 dimensiones del clima</CardDescription>
              </CardHeader>
              <CardContent>
                <div id="climate-radar-chart" className="h-[300px] bg-white rounded">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#0ea5e9"
                        fill="#0ea5e9"
                        fillOpacity={0.4}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base">Score por Departamento</CardTitle>
                <CardDescription>Comparativa de clima por área</CardDescription>
              </CardHeader>
              <CardContent>
                <div id="climate-bar-chart" className="h-[300px] bg-white rounded">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => [`${v}/100`, 'Score']} />
                      <Bar dataKey="score" fill="#10b981" name="Score" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Heatmap */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detalle por Dimensiones</CardTitle>
              <CardDescription>Heatmap: departamentos vs dimensiones (rojo &lt;60, verde &gt;80)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px] text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-2.5 text-left font-medium">Departamento</th>
                      {LIKERT_DIMENSIONS.map((dim) => (
                        <th key={dim} className="px-3 py-2.5 text-center font-medium">
                          {DIMENSION_LABELS[dim]}
                        </th>
                      ))}
                      <th className="px-3 py-2.5 text-center font-medium">Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapRows.map((row) => (
                      <tr key={row.department} className="border-b last:border-0">
                        <td className="px-4 py-2.5 font-medium">{row.department}</td>
                        {LIKERT_DIMENSIONS.map((dim) => (
                          <td
                            key={dim}
                            className={cn(
                              'px-3 py-2.5 text-center font-medium',
                              getCellBgClass(row.scores[dim] ?? 0)
                            )}
                          >
                            {row.scores[dim] ?? '—'}
                          </td>
                        ))}
                        <td
                          className={cn(
                            'px-3 py-2.5 text-center font-semibold',
                            getCellBgClass(row.average)
                          )}
                        >
                          {row.average}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Comentarios abiertos */}
          {openComments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Comentarios Abiertos</CardTitle>
                <CardDescription>
                  Feedback cualitativo (anonimizado, se muestra departamento si está disponible)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] space-y-3 overflow-y-auto pr-2">
                  {openComments.map((c, i) => (
                    <div
                      key={i}
                      className="rounded-lg border bg-muted/30 p-3 text-sm"
                    >
                      {c.department && (
                        <p className="mb-1 text-xs font-medium text-muted-foreground">
                          {c.department}
                        </p>
                      )}
                      <p className="text-foreground">{c.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      </div>
    </div>
  );
}
